import clsx from "clsx";
import { ChevronRight, Clock, FileText, Fuel, LayoutDashboard, Mountain, Swords } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";

import type {
  DeployedUnit,
  Logistics,
  LogisticsAssumptions,
  LogisticsMaintenance,
  MaintenancePhase,
  MaintenancePosture,
  MaterielFamily,
} from "../../api/types";
import { designateSoon } from "../../components/attention";
import { Callout } from "../../components/Callout";
import { Collapsible } from "../../components/Collapsible";
import { EmptyState, Spinner } from "../../components/EmptyState";
import { PanelGroup, PanelRow, StatTile } from "../../components/panel";
import { Motion, Presence } from "../../components/presence";
import { Badge, Button, FloatingPanel } from "../../components/ui";
import { VIEW_META } from "../../components/viewMeta";
import { Waiting } from "../../components/Waiting";
import { HelpButton } from "../../help/HelpButton";
import { unitDesignation } from "../../i18n/designation";
import { formatNumber, k, t } from "../../i18n/i18n";
import { colorTone, FLEET_KEYS, GREEN_ABOVE, maintenanceColor, percentText, REQUIRED_PHASES } from "../../maintenance";
import { zoneRefusalOf } from "../../map/zones/zoneRefusal";
import { ZoneWaitCallout } from "../../map/zones/ZoneWaitCallout";
import { useUi } from "../../state/ui";
import { OpenMissionDataButton } from "../openMissionData";
import { AssumptionsBadge, customOf, ResetAssumptions, Slider, type SliderSpec, useSaveAssumptions } from "../logistics/assumptions";
import { ZOOM } from "../coherence/CoherencePanel";
import { mobilityLabel, SituationLine, useLogistics } from "../logistics/LogisticsPanel";
import { CapopsStatusIcon, initialFamily } from "./DtoCurve";
import { FleetTable, type Preview, previewed } from "./FleetTable";
import { MaintenanceDashboard } from "./MaintenanceDashboard";
import { CapopsTable, capopsRows, type CapopsRow, DtoTable } from "./PhaseTables";

/** Maintenance S4 (vue « Maintenance », en-tête « CAPOPS / DTO », refonte UX du 04/11/2026) : la CAPOPS des unités
 *  (maillon le plus faible du personnel, des matériels et des munitions, aux couleurs de l'iPad) de T1 à T3, puis, en
 *  détail technique replié, la DTO (disponibilité technique opérationnelle) par parc de matériel de T0 à T3, jugée
 *  contre la DTO requise du parc à chaque temps (réglée parc par parc et temps par temps ; à la requise ou au-dessus,
 *  jamais une alerte). Même grammaire que les autres vues sur carte : contexte en une ligne, synthèse en tuiles,
 *  contenu en tableaux, réglages repliés, définitions dans l'aide et les info-bulles. Indicateurs de nature
 *  différente du ravitaillement (vue Logistique), d'où une vue à part, qui lit pourtant la même réponse du serveur
 *  (`useLogistics` : même cache, même invalidation). « Ouvrir la maintenance » d'un constat de cohérence désigne le
 *  parc (`focusMaintenance(family)`), celui d'une fiche d'unité désigne l'unité (`focusMaintenance(null, key)`). */

/** Hypothèses de la chute de DTO (avaries, dommages de combat) et de la maintenance opérationnelle (remises en état). */
const SLIDERS: SliderSpec[] = [
  { key: "failures_per_day", label: k("Taux d'avaries par jour au contact"), digits: 1, unit: k("% du parc") },
  { key: "combat_damage_per_day", label: k("Dommages de combat par jour au contact"), digits: 1, unit: k("% du parc") },
  { key: "failures_per_100km", label: k("Avaries pour 100 km"), digits: 1, unit: k("% du parc") },
  { key: "repairs_per_day", label: k("Remises en état par jour (NTI1-NTI2)"), digits: 0, unit: k("% des matériels indisponibles") },
];

export const MAINTENANCE_KEYS: readonly (keyof LogisticsAssumptions)[] = [...SLIDERS.map((s) => s.key), ...FLEET_KEYS];
const RATE_KEYS: readonly (keyof LogisticsAssumptions)[] = SLIDERS.map((s) => s.key);

/** Posture et coefficients d'engagement du calcul (`maintenance.py` : CONTACT_ENGAGEMENT, REAR_ENGAGEMENT,
 *  DESTROYED_SHARE) : au contact, avaries et dommages de combat × 1, × 0,85 ou × 0,75 ; hors contact, avaries × 0,25 et
 *  aucun dommage de combat ; un quart des dommages de combat sont des pertes définitives. */
const POSTURE: Record<MaintenancePosture, string> = {
  offensive: k(
    "Offensive : au contact, taux d'avaries et de dommages de combat pleins (× 1) ; hors contact, avaries au quart (× 0,25), sans dommages de combat ; un quart des dommages de combat sont des pertes définitives.",
  ),
  defensive: k(
    "Défensive : au contact, taux d'avaries et de dommages de combat × 0,75 ; hors contact, avaries au quart (× 0,25), sans dommages de combat ; un quart des dommages de combat sont des pertes définitives.",
  ),
  mixed: k(
    "Mixte : au contact, taux d'avaries et de dommages de combat × 0,85 ; hors contact, avaries au quart (× 0,25), sans dommages de combat ; un quart des dommages de combat sont des pertes définitives.",
  ),
};

/** « T1 46 h · T2 93 h · T3 185 h » (durées tirées des GDH du dossier). */
const hoursText = (hours: LogisticsMaintenance["hours"], separator = " · ") =>
  REQUIRED_PHASES.map((p) => t("{phase} {hours} h", { phase: p, hours: formatNumber(Math.round(hours[p])) })).join(separator);

/** Posture du calcul en un mot (le détail des coefficients en info-bulle et dans l'aide). */
const POSTURE_SHORT: Record<MaintenancePosture, string> = {
  offensive: k("Offensive"),
  defensive: k("Défensive"),
  mixed: k("Mixte"),
};

/** Mention de la ligne de contexte : icône grise de 12 px, texte court, détail en info-bulle (et pour les lecteurs
 *  d'écran). */
function ContextItem({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5" title={title}>
      <span className="shrink-0 text-ink-muted" aria-hidden>
        {icon}
      </span>
      <span>{children}</span>
      <span className="sr-only">{title}</span>
    </span>
  );
}

/** Contexte du calcul en une ligne compacte (durées des temps, posture, mobilité), sous le titre « En direct depuis la
 *  carte », comme la vue Logistique ; chaque mention a son détail en info-bulle. `actions` : à droite du titre, le
 *  bouton « Tableau de bord » (en haut de la vue, sans serrer le titre de l'en-tête, long en anglais). */
export function MaintenanceContext({ data, m, actions }: { data: Logistics; m: LogisticsMaintenance; actions?: ReactNode }) {
  return (
    <PanelGroup title={t("En direct depuis la carte · temps {phase}", { phase: data.phase })} actions={actions} flush>
      <p className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 pb-1 text-meta leading-snug text-ink-soft">
        <ContextItem icon={<Clock size={12} />} title={t("Durée des temps d'après les GDH du dossier : {hours}.", { hours: hoursText(m.hours, ", ") })}>
          <span className="tabular-nums">{hoursText(m.hours)}</span>
        </ContextItem>
        <ContextItem icon={<Swords size={12} />} title={t(POSTURE[m.posture])}>
          {t(POSTURE_SHORT[m.posture])}
        </ContextItem>
        <ContextItem icon={<Mountain size={12} />} title={mobilityLabel(data.situation)}>
          {t("Mobilité {value}", { value: formatNumber(data.situation.mobility, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) })}
        </ContextItem>
      </p>
    </PanelGroup>
  );
}

/** Noms des unités amies : ceux de la logistique du temps affiché, sinon ceux du dispositif (une unité absente du temps
 *  affiché reste nommée). */
export function unitNames(data: Logistics, deployed: DeployedUnit[] = []): Map<string, string> {
  const names = new Map<string, string>();
  for (const u of deployed) if (u.side === "AMI") names.set(u.key, u.display_name ?? u.designation);
  for (const l of data.lines) names.set(l.key, l.name);
  return names;
}

/** Le cœur de la vue, sans la requête (essais). Une seule hiérarchie, celle des autres vues (demande d'Alexis du
 *  04/11/2026 : « harmonise-le avec les autres vues ») : synthèse (CAPOPS la plus basse, unités affaiblies), puis le
 *  tableau « CAPOPS des unités », tous les temps d'un coup d'œil (« l'utilisateur doit pouvoir les voir pour tous les
 *  T »), les plus affaiblies d'abord ; puis, replié par défaut, le « Détail technique : DTO par parc » pour le soutien :
 *  tableau des parcs de T0 à T3, « Régler la DTO requise » (`settings` : courbe et curseurs du parc choisi, ouvert par
 *  le clic sur un parc) et les taux (`rates`). Les définitions sont dans l'aide « ? » et les info-bulles.
 *  « Ouvrir la maintenance » désigne le parc (constat de cohérence : détail technique ouvert, parc choisi) ou l'unité
 *  (fiche d'unité). `preview` : curseurs en cours de réglage, montrés aussitôt ; `chosen` / `onChoose` : parc réglé ;
 *  `units` : dispositif, pour nommer une unité absente du temps affiché ; `onOpenUnit` : fiche de l'unité. */
export function MaintenanceBody({
  data,
  phase,
  preview = {},
  chosen: chosenProp,
  onChoose,
  settings,
  rates,
  units: deployed = [],
  onOpenUnit,
  technicalOpen = false,
}: {
  data: Logistics;
  phase: MaintenancePhase;
  preview?: Preview;
  chosen?: MaterielFamily | null;
  onChoose?: (family: MaterielFamily) => void;
  /** Réglage du parc choisi (courbe et curseurs), dans « Régler la DTO requise ». */
  settings?: ReactNode;
  /** Taux d'avaries et de remises en état, à la fin du détail technique. */
  rates?: ReactNode;
  units?: DeployedUnit[];
  onOpenUnit?: (key: string, phases: CapopsRow["phases"]) => void;
  technicalOpen?: boolean;
}) {
  const raw = data.maintenance;
  const m = raw ? previewed(raw, data.assumptions, preview) : raw;
  const focus = useUi((s) => s.maintenanceFocus);
  const focusUnit = useUi((s) => s.maintenanceUnit);
  const focusMaintenance = useUi((s) => s.focusMaintenance);
  // Bureau de la logistique qui lit la DTO : B4 au corps d'armée, S4 aux autres niveaux.
  const corps = useUi((s) => s.deploymentParams?.level) === "CORPS";
  const [own, setOwn] = useState<MaterielFamily | null>(() => (m ? initialFamily(m, focus) : null));
  const [technical, setTechnical] = useState(technicalOpen || Boolean(focus));
  const [tuning, setTuning] = useState(Boolean(focus));
  const chosen = chosenProp !== undefined && chosenProp !== null ? chosenProp : own;
  const setChosen = (family: MaterielFamily) => {
    setOwn(family);
    onChoose?.(family);
  };

  // Parc ou unité désigné par « Ouvrir la maintenance » (constat de cohérence, fiche d'unité) : le parc ouvre le
  // détail technique et son réglage, l'unité est amenée à l'écran ; la cible est mise en surbrillance (fondu), puis
  // oubliée. La recherche de la cible n'est annulée qu'au démontage : `focusMaintenance(null)` relance l'effet
  // aussitôt et ne doit pas l'interrompre.
  const cancel = useRef<(() => void) | null>(null);
  useEffect(() => () => cancel.current?.(), []);
  useEffect(() => {
    if ((!focus && !focusUnit) || !m) return;
    if (focus && m.families.some((f) => f.family === focus)) {
      setTechnical(true);
      setTuning(true);
      setChosen(focus);
      cancel.current?.();
      cancel.current = designateSoon(`[data-maintenance-family="${focus}"]`, "ring");
    } else if (focusUnit && m.units[focusUnit]) {
      cancel.current?.();
      cancel.current = designateSoon(`[data-maintenance-unit="${focusUnit.replace(/["\\]/g, "\\$&")}"]`, "ring");
    }
    focusMaintenance(null);
  }, [focus, focusUnit, m]);

  if (!m || !m.families.length) return null;
  const rows = capopsRows(m, unitNames(data, deployed), phase);
  const atPhase = rows.flatMap((r) => (r.phases[phase] ? [{ row: r, capops: r.phases[phase]! }] : []));
  const weakest = atPhase.reduce<(typeof atPhase)[number] | undefined>((a, b) => (!a || b.capops.capops < a.capops.capops ? b : a), undefined);
  const weakened = atPhase.filter((u) => Math.round(u.capops.capops) <= GREEN_ABOVE).length;
  const below = m.families.filter((f) => f.below);
  const choose = (family: MaterielFamily) => {
    setChosen(family);
    setTuning(true);
  };
  return (
    <>
      {weakest && (
        <PanelGroup flush>
          <div className="grid grid-cols-2 gap-2 px-4 py-1">
            <StatTile
              icon={<CapopsStatusIcon percent={weakest.capops.capops} size={12} />}
              value={percentText(weakest.capops.capops)}
              tone={colorTone(maintenanceColor(weakest.capops.capops))}
              label={t("CAPOPS la plus basse en {phase}", { phase })}
              hint={unitDesignation(weakest.row.name)}
            />
            <StatTile
              value={formatNumber(weakened)}
              unit={t("sur {n}", { n: formatNumber(atPhase.length) })}
              tone={weakened ? "warn" : "neutral"}
              label={t("Unités à 70 % ou moins en {phase}", { phase })}
              hint={weakened ? t("CAPOPS orange ou rouge") : t("Toutes au-dessus de 70 %")}
            />
          </div>
        </PanelGroup>
      )}

      {rows.length > 0 && (
        <PanelGroup title={t("CAPOPS des unités")} count={rows.length}>
          <CapopsTable rows={rows} phase={phase} onOpen={onOpenUnit} />
        </PanelGroup>
      )}

      <Collapsible
        flush
        open={technical}
        onOpenChange={setTechnical}
        title={t("Détail technique : DTO par parc")}
        trailing={
          below.length ? (
            <Badge tone="warn">{t("{n} sous la DTO requise", { n: formatNumber(below.length) })}</Badge>
          ) : (
            <span className="text-meta text-ink-muted">{corps ? "B4" : "S4"}</span>
          )
        }
      >
        <PanelGroup className="border-t border-line">
          <DtoTable m={m} phase={phase} chosen={tuning ? chosen : null} onChoose={choose} />
          {m.reduced && (
            <div className="px-2 pt-1.5">
              <SituationLine icon={<Clock size={12} />}>
                <span
                  title={t("Opération de plus de {days} jours : la chute de DTO est ramenée à cette durée de référence (taux journaliers divisés d'autant).", {
                    days: formatNumber(m.reference_days),
                  })}
                >
                  {t("Chute de DTO ramenée à un engagement de {days} jours", { days: formatNumber(m.reference_days) })}
                </span>
              </SituationLine>
            </div>
          )}
        </PanelGroup>
        {settings && (
          <Collapsible open={tuning} onOpenChange={setTuning} title={t("Régler la DTO requise")} trailing={<AssumptionsBadge data={data} custom={customOf(data, FLEET_KEYS)} />}>
            {settings}
          </Collapsible>
        )}
        {rates}
      </Collapsible>
    </>
  );
}

export function MaintenancePanel() {
  const exerciseId = useUi((s) => s.exerciseId);
  const phase = useUi((s) => s.phase);
  const setView = useUi((s) => s.setView);
  const setPhase = useUi((s) => s.setPhase);
  const select = useUi((s) => s.select);
  const flyTo = useUi((s) => s.requestFlyTo);
  const level = useUi((s) => s.deploymentParams?.level);
  const { query, key, opo, complete, centac, deployment } = useLogistics(exerciseId, phase);
  const { save, reverts } = useSaveAssumptions(exerciseId, phase, opo, query, key);
  const data = query.data;
  const m = data?.maintenance;
  const custom = data ? customOf(data, RATE_KEYS) : false;
  // Curseurs parc × temps en cours de réglage : montrés aussitôt, oubliés dès que le serveur répond (ou refuse).
  const [preview, setPreview] = useState<Preview>({});
  const [chosen, setChosen] = useState<MaterielFamily | null>(null);
  // Tableau de bord : grande fenêtre au-dessus de la carte, tous les temps, toutes les unités et tous les parcs.
  const [dashboard, setDashboard] = useState(false);
  const focusMaintenance = useUi((s) => s.focusMaintenance);
  useEffect(() => setPreview({}), [data?.updated_at, reverts]);
  const ready = Boolean(data && m && m.families.length && !centac && data.totals);
  useEffect(() => {
    if (!ready) setDashboard(false);
  }, [ready]);

  // Clic sur une unité du tableau : sa fiche s'ouvre et la carte s'y centre, au temps affiché ou, si l'unité n'y est
  // pas posée, au premier temps où elle l'est (comme un constat de cohérence qui désigne une unité).
  const openUnit = (unitKey: string, phases: CapopsRow["phases"]) => {
    const unit = deployment?.units.find((u) => u.key === unitKey);
    if (!unit) return;
    const at = unit.positions[phase] ? phase : REQUIRED_PHASES.find((p) => unit.positions[p] && phases[p]);
    const position = at ? unit.positions[at] : undefined;
    if (!at || !position) return;
    if (at !== phase) setPhase(at);
    const lngLat: [number, number] = [position.lon, position.lat];
    select({ layer: "unit", code: unit.code, lngLat });
    flyTo(lngLat, ZOOM[level ?? ""] ?? 11);
  };

  return (
    <FloatingPanel
      enter="left"
      width="view"
      icon={VIEW_META.maintenance.icon}
      title={t("CAPOPS / DTO")}
      actions={<HelpButton screen="maintenance" buttonSize="sm" size={16} />}
    >
      <Presence show={dashboard && ready}>
        {dashboard && ready && data && m && (
          <MaintenanceDashboard
            m={m}
            rows={capopsRows(m, unitNames(data, deployment?.units), data.phase)}
            units={deployment?.units ?? []}
            phase={data.phase}
            onClose={() => setDashboard(false)}
            onOpenUnit={(unitKey, phases) => {
              setDashboard(false);
              focusMaintenance(null, unitKey);
              openUnit(unitKey, phases);
            }}
            onOpenFamily={(family) => {
              setDashboard(false);
              focusMaintenance(family);
            }}
          />
        )}
      </Presence>
      {!exerciseId || !complete ? (
        <Motion origin="fade">
          <EmptyState variant="inline" action={<OpenMissionDataButton />}>
            <p className="text-ink-soft">
              {t(
                "La vue suit, de T0 à T3, la CAPOPS (capacité opérationnelle) des unités du dispositif affiché sur la carte et, en détail technique, la DTO (disponibilité technique opérationnelle) de chaque parc de matériel.",
              )}
            </p>
            <p className="mt-2">
              {!exerciseId ? t("Pour suivre la maintenance, créez d'abord le dossier.") : t("Complétez les Données initiales.")}
            </p>
          </EmptyState>
        </Motion>
      ) : query.isError ? (
        <div className="px-4 py-4">
          {zoneRefusalOf(query.error) ? (
            <ZoneWaitCallout message={query.error.message} error={query.error} />
          ) : (
            <Callout tone="danger">{query.error.message}</Callout>
          )}
        </div>
      ) : !data ? (
        <div className="flex items-center gap-2 px-4 py-6 text-ui text-ink-muted">
          <Spinner decorative /> {<Waiting>{t("Calcul de la maintenance…")}</Waiting>}
        </div>
      ) : centac ? (
        <Motion origin="fade" className="px-4 py-4">
          <Callout
            tone="info"
            action={
              <Button variant="secondary" size="sm" onClick={() => setView("order")}>
                <FileText size={14} /> {t("Ouvrir l'ordre")}
              </Button>
            }
          >
            {t("En rotation CENTAC, la CAPOPS vient des résultats de chaque OPO de la rotation : la maintenance n'est pas estimée.")}
          </Callout>
        </Motion>
      ) : !data.totals || !m ? (
        <Motion origin="fade">
          <p className="flex items-start gap-2 border-b border-line px-4 py-3.5 text-ui leading-relaxed text-ink-soft">
            <Swords size={14} className="mt-0.5 shrink-0 text-ink-muted" />
            {data.empty === "no_ami"
              ? t("Aucune unité amie n'est placée au temps {phase}.", { phase: data.phase })
              : t("Aucun ennemi n'est placé au temps {phase} : l'engagement des unités se mesure face au dispositif ennemi.", {
                  phase: data.phase,
                })}
          </p>
        </Motion>
      ) : (
        <Motion origin="fade" className={clsx("transition-opacity", query.isFetching && "opacity-80")}>
          <MaintenanceContext
            data={data}
            m={m}
            actions={
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setDashboard(true)}
                title={t("Vue générale : CAPOPS des unités et DTO des parcs à tous les temps")}
                aria-haspopup="dialog"
                data-maintenance-dashboard-open=""
              >
                <LayoutDashboard size={14} /> {t("Tableau de bord")}
              </Button>
            }
          />

          <MaintenanceBody
            data={data}
            phase={data.phase}
            preview={preview}
            chosen={chosen}
            onChoose={setChosen}
            units={deployment?.units}
            onOpenUnit={openUnit}
            settings={
              <FleetTable
                data={data}
                phase={data.phase}
                family={chosen}
                onFamily={setChosen}
                preview={preview}
                reverts={reverts}
                pending={save.isPending}
                onPreview={(family, changes) => {
                  setChosen(family);
                  setPreview((p) => ({ ...p, ...changes }));
                }}
                onCommit={(changes) => save.mutate(changes)}
              />
            }
            rates={
              <Collapsible title={t("Taux d'avaries et de remises en état")} trailing={<AssumptionsBadge data={data} custom={custom} />}>
                <div className="space-y-3">
                  {SLIDERS.map((spec) => (
                    <Slider
                      key={`${spec.key}-${reverts}`}
                      spec={spec}
                      value={data.assumptions[spec.key]}
                      bounds={data.bounds[spec.key]}
                      disabled={save.isPending}
                      onCommit={(value) => save.mutate({ [spec.key]: value })}
                    />
                  ))}
                  <ResetAssumptions data={data} keys={RATE_KEYS} custom={custom} pending={save.isPending} onReset={(v) => save.mutate(v)} />
                </div>
              </Collapsible>
            }
          />

          <PanelGroup flush>
            <PanelRow
              icon={<Fuel size={14} />}
              title={t("Logistique")}
              detail={t("Carburant, munitions et drones : besoins estimés dans leur vue.")}
              state={<ChevronRight size={14} className="text-ink-muted" aria-hidden />}
              onClick={() => setView("logistics")}
              clickTitle={t("Ouvrir la logistique")}
            />
          </PanelGroup>

          <p className="border-t border-line px-4 py-3 text-meta leading-relaxed text-ink-muted">
            {t("Prévision de planification : taux et DTO requises réglables, pas des valeurs réglementaires.")}
          </p>
        </Motion>
      )}
    </FloatingPanel>
  );
}
